/**
 * DCF resubmittals: the Finance board's reads and writes (backend/src/routes/financeWorkspace.js "DCF resubmittals",
 * services/finance/resubmittalBoard.js cardOf). Finance starts a resubmittal, states the kind and the correction, hands
 * it to UMED, or withdraws it before it is sent. Every act is one POST the server decides again.
 */
import api from '../../services/api';
import type { ApiResult } from './financeApi';
import { boardQueryString } from '../scheduling/boardApi';

export type ResubmittalColumnKey = 'WITH_FINANCE' | 'WITH_UMED' | 'WITH_DCF' | 'DCF_DECLINED' | 'APPROVED' | 'WITHDRAWN';
export type ResubmittalKind = 'CORRECTED' | 'FULL';
export type ResubmittalRequester = 'FACILITY' | 'RESPONSIBLE_PARTY' | 'DCF' | 'SFS' | 'OTHER';

/** The correction Finance states (CORRECTED only): whole cents, never derived. */
export interface CorrectedTerms {
  months: number;
  monthly_cents: number;
  total_cents: number;
  starts_on?: string | null;
  ends_on?: string | null;
  terms_text?: string | null;
}

export interface ResubmittalCardRow {
  id: string;
  case_id: string;
  patient_id: string;
  name: string | null;
  facility_id: string | null;
  facility_name: string | null;
  state: ResubmittalColumnKey;
  owner: 'FINANCE' | 'UMED' | null;
  /** null = a legacy card whose kind was never stated. */
  kind: ResubmittalKind | null;
  corrected_terms: CorrectedTerms | null;
  requester: ResubmittalRequester | string;
  reason: string | null;
  reasons_count: number;
  requested_at: string | null;
  requested_by_name: string | null;
  handed_to_umed_at: string | null;
  handed_by_name: string | null;
  submission_count: number;
  last_submitted_at: string | null;
  outcome: string | null;
  outcome_recorded_at: string | null;
  completed_at: string | null;
  withdrawn_at: string | null;
  withdrawn_reason: string | null;
  withdrawn_by_name: string | null;
  approved_on: string | null;
  /** What DCF was last sent; `total` is dollars (a packet field), not cents. */
  prior_terms: { terms: string | null; total: number | null; date: string | null; date_kind: string | null } | null;
  packet: { state: string | null; blockers: string[] } | null;
  handoff_action_id: string | null;
  /** Whether Finance has a handoff for this claim, in words (an approved corrected one is amended only on one that exists). */
  finance_handoff_words?: string | null;
  withdrawable_by: Array<'FINANCE' | 'UMED'>;
}

export interface ResubmittalColumnRead {
  key: ResubmittalColumnKey;
  label: string;
  owner: 'FINANCE' | 'UMED' | null;
  history: boolean;
  what: string;
  count: number;
  cards: ResubmittalCardRow[];
}

export interface ResubmittalBoardPage {
  columns: ResubmittalColumnRead[];
  page: number;
  limit: number;
  side: string;
  can: { manage: boolean };
  requesters: ResubmittalRequester[];
  kinds: ResubmittalKind[];
}

/**
 * One UMED Finance may start a resubmittal of (resubmittalBoard.candidates, 2026-10-21): every case the start accepts,
 * whether or not Finance holds or is owed its handoff. Since 2026-10-29 (Isaiah Cotto, amending REV 01) not only an
 * approved one: UMED SUBMITTED TO DCF, a DCF resubmission sent again, or SFS CORRECTION too (`start_status`).
 */
export type ResubmittalStartStatus = 'APPROVED' | 'DCF_SUBMITTED' | 'RESUB_DCF_SUBMITTED' | 'SFS_CORRECTION';
export interface ResubmittalCandidate {
  case_id: string;
  patient_id: string;
  name: string | null;
  facility_name: string | null;
  stage?: string | null;
  /** The status the start accepts it as (resubmittalState.START_STATUSES); absent from an older server (then APPROVED). */
  start_status?: ResubmittalStartStatus | null;
  /** The case's UMED status word. */
  status_word?: string | null;
  /** Verifier D1: a fax of the case holds the start until it is recorded (IN_FLIGHT, AWAITING_RECORD), with its words. */
  fax_wait?: 'IN_FLIGHT' | 'AWAITING_RECORD' | null;
  startable?: boolean;
  wait?: string;
  /** The day eMerge's own approval act recorded; null when the approval is Monday's word only. */
  approved_on: string | null;
  /** Where the approval came from: eMerge's act, Monday's board, or neither on record. */
  approved_in?: 'EMERGE' | 'MONDAY' | null;
  /** The board's approval day, when eMerge holds no approval act of its own. */
  board_approved_on?: string | null;
  /** What Finance holds of the handoff: received, owed (not yet received), or none at all. */
  handoff?: 'RECEIVED_IN_FINANCE' | 'OWED' | 'NONE';
  handoff_state: string | null;
  handoff_action_id: string | null;
  /** The same, in words: "Approved in Monday, no Finance handoff yet". */
  basis?: string;
}

export interface ResubmittalCandidatePage {
  items: ResubmittalCandidate[];
  /** Another page of matches exists: narrow by name or building. */
  more?: boolean;
  limit?: number;
}

export interface ResubmittalQuery { q?: string; column?: string; page?: number; limit?: number }

export interface StartBody {
  umed_case_id: string;
  requester: ResubmittalRequester;
  reason: string;
  kind: ResubmittalKind;
  corrected_terms?: CorrectedTerms;
}

const base = '/finance/resubmittals';

export const resubmittalsApi = {
  board: async (p: ResubmittalQuery, signal?: AbortSignal): Promise<ApiResult<ResubmittalBoardPage>> => {
    const url = `${base}${boardQueryString({ q: p.q, column: p.column, page: p.page, limit: p.limit })}`;
    return (await (signal ? api.get(url, undefined, { signal }) : api.get(url))) as ApiResult<ResubmittalBoardPage>;
  },
  candidates: async (q: string): Promise<ApiResult<ResubmittalCandidatePage>> =>
    (await api.get(`${base}/candidates${boardQueryString({ q: q || undefined })}`)) as ApiResult<ResubmittalCandidatePage>,
  card: async (id: string): Promise<ApiResult<ResubmittalCardRow>> =>
    (await api.get(`${base}/${encodeURIComponent(id)}`)) as ApiResult<ResubmittalCardRow>,
  start: async (body: StartBody): Promise<ApiResult<unknown>> => (await api.post(base, body)) as ApiResult<unknown>,
  correction: async (id: string, body: { kind: ResubmittalKind; corrected_terms?: CorrectedTerms }): Promise<ApiResult<unknown>> =>
    (await api.post(`${base}/${encodeURIComponent(id)}/correction`, body)) as ApiResult<unknown>,
  addReason: async (id: string, reason: string): Promise<ApiResult<unknown>> =>
    (await api.post(`${base}/${encodeURIComponent(id)}/reasons`, { reason })) as ApiResult<unknown>,
  handToUmed: async (id: string): Promise<ApiResult<unknown>> =>
    (await api.post(`${base}/${encodeURIComponent(id)}/hand-to-umed`, {})) as ApiResult<unknown>,
  withdraw: async (id: string, reason: string): Promise<ApiResult<unknown>> =>
    (await api.post(`${base}/${encodeURIComponent(id)}/withdraw`, { reason })) as ApiResult<unknown>,
};
